<style lang="scss">
$color-black: #000000;
$color-white: #ffffff;
$color-grey: #dddddd;

.editor {
  &__content {
    word-wrap: break-word;
    height: 100%;
    min-height: 160px;

    > div {
      min-height: 160px;
      outline: none;
    }

    * {
      caret-color: currentColor;
    }

    pre {
      padding: 0.7rem 1rem;
      border-radius: 5px;
      background: $color-black;
      color: $color-white;
      font-size: 0.8rem;
      overflow-x: auto;

      code {
        display: block;
      }
    }

    p.is-empty:first-child::before {
      content: attr(data-empty-text);
      float: left;
      color: #aaa;
      pointer-events: none;
      height: 0;
      font-style: italic;
    }

    p code {
      display: inline-block;
      padding: 0 0.4rem;
      border-radius: 5px;
      font-size: 0.8rem;
      font-weight: bold;
      background: rgba($color-black, 0.1);
      color: rgba($color-black, 0.8);
    }

    ul,
    ol {
      padding-left: 3rem;
    }

    li > p,
    li > ol,
    li > ul {
      margin: 0;
    }

    a {
      color: inherit;
    }

    blockquote {
      border-left: 3px solid rgba($color-black, 0.1);
      color: rgba($color-black, 0.8);
      padding-left: 0.8rem;
      font-style: italic;

      p {
        margin: 0;
      }
    }

    img {
      max-width: 100%;
      border-radius: 3px;
    }

    table {
      border-collapse: collapse;
      table-layout: fixed;
      width: 100%;
      margin: 0;
      overflow: hidden;

      td,
      th {
        min-width: 1em;
        border: 2px solid $color-grey;
        padding: 3px 5px;
        vertical-align: top;
        box-sizing: border-box;
        position: relative;

        > * {
          margin-bottom: 0;
        }
      }

      th {
        font-weight: bold;
        text-align: left;
      }

      .selectedCell:after {
        z-index: 2;
        position: absolute;
        content: "";
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background: rgba(200, 200, 255, 0.4);
        pointer-events: none;
      }

      .column-resize-handle {
        position: absolute;
        right: -2px;
        top: 0;
        bottom: 0;
        width: 4px;
        z-index: 20;
        background-color: #adf;
        pointer-events: none;
      }
    }

    .tableWrapper {
      margin: 1em 0;
      overflow-x: auto;
    }

    .resize-cursor {
      cursor: ew-resize;
      cursor: col-resize;
    }
  }
}

.menubar {
  transition: visibility 0.2s 0.4s, opacity 0.2s 0.4s;

  &.is-hidden {
    visibility: hidden;
    opacity: 0;
  }

  &.is-focused {
    visibility: visible;
    opacity: 1;
    transition: visibility 0.2s, opacity 0.2s;
  }

  &__button {
    font-weight: bold;
    display: inline-flex;
    background: transparent;
    border: 0;
    color: $color-black;
    padding: 0.5rem;
    margin-right: 0.2rem;
    border-radius: 3px;
    cursor: pointer;
    outline: none;

    &:hover {
      background-color: rgba($color-black, 0.05);
    }

    &.is-active {
      background-color: rgba($color-black, 0.1);
    }
  }
}

.bg-custom-color {
    background-color: #e20a15;
}

.bg-custom-color-hover:hover {
    background-color: #cc0611;
}

.text-custom-color {
    color: #e20a15;
}

</style>
<template>
  <div>
    <div v-show="isLoading()">
      <div
        class="flex justify-center items-center bg-gray-200 border-t border-b border-gray-400 mb-8"
        role="alert"
      >
        <i class="fal fa-fw fa-spinner fa-spin"></i>
        <span class="text-gray-700 text-sm font-semibold p-2"
          >Cargando cotización...</span
        >
      </div>
    </div>

    <div v-if="cotizacion">
      <div class="flex flex-col mb-8">
        <div class="flex flex-wrap items-start">
          <h3 class="bikesport-subtitle text-lg font-light text-custom-color mr-2">
            Ticket <i class="fal fa-hashtag"></i> {{ cotizacion.ticket }}
          </h3>
        </div>
      </div>

      <div class="flex flex-wrap">
        <!-- Datos del Producto -->
        <div
          class="flex flex-col w-full lg:flex-1 border-t border-gray-400 mr-0 mb-4 lg:mr-2"
        >
          <div v-if="cotizacion && cotizacion.producto">
            <div class="bg-gray-100 border-b border-gray-400 py-2 px-2">
              <p class="uppercase text-sm font-light text-gray-800">
                Producto cotizado
              </p>
            </div>

            <div class="flex py-6 justify-center">
              <div v-if="cotizacion && cotizacion.marca">
                <img
                  class="w-full max-w-sm"
                  :src="
                    require(`@/assets/marcas/${cotizacion.marca.toLowerCase()}/logo-cotizacion.png`)
                  "
                  :alt="cotizacion.marca"
                  :title="cotizacion.marca"
                />
              </div>
            </div>

            <div class="px-4 pt-4 flex">
              <div class="w-1/2 text-center overflow-hidden mr-4">
                <img :src="cotizacion.producto.imagen" alt="producto" />
              </div>
              <div class="w-1/2 flex flex-col leading-normal text-gray-800">
                <span class="text-lg font-semibold text-gray-700">{{
                  cotizacion.producto.nombre
                }}</span>
                <div v-if="cotizacion.producto.es_proximo">
                  <span class="block mt-1 font-light">Próximamente</span>
                </div>
                <div v-else>
                  <div v-if="cotizacion.producto.en_oferta">
                    <span
                      v-if="
                        cotizacion.producto.precio_regular !== '' &&
                        parseInt(cotizacion.producto.precio_regular) > 0
                      "
                      class="block mt-1 text-xs font-light text-gray-600 line-through"
                    >
                      $ {{ cotizacion.producto.precio_regular | prettyNumber }}
                    </span>
                    <span class="block font-light">
                      <span v-if="cotizacion.producto.tiene_financiamiento"
                        >Desde
                      </span>
                      $ {{ cotizacion.producto.precio_oferta | prettyNumber }}
                    </span>
                  </div>
                  <div v-else>
                    <span class="mt-1 font-light">
                      $ {{ cotizacion.producto.precio | prettyNumber }}
                    </span>
                  </div>
                </div>
                <span v-if="cotizacion.color" class="mb-1 text-sm font-light">
                  <i class="fal fa-palette fa-fw text-gray-600"></i>
                  {{ cotizacion.color }}
                </span>
                <span v-if="cotizacion.talla" class="mb-1 text-sm font-light">
                  <i class="fal fa-tag fa-fw text-gray-600"></i>
                  Talla {{ cotizacion.talla }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="cotizacion && cotizacion.prefijo === 'SCR'">
            <div class="bg-gray-100 border-b border-gray-400 py-2 px-2">
              <p class="uppercase text-sm font-light text-gray-800">
                Repuesto cotizado
              </p>
            </div>

            <div class="px-4 pt-4 flex">
              <div
                v-if="
                  cotizacion.extra &&
                  cotizacion.extra.archivos &&
                  cotizacion.extra.archivos.length > 0
                "
                class="w-1/2 text-center overflow-hidden mr-4"
              >
                <img :src="cotizacion.extra.archivos[0]" alt="producto" />
              </div>
              <div class="w-1/2 flex flex-col leading-normal text-gray-800">
                <span class="text-lg font-semibold text-gray-700">{{
                  cotizacion.modelo
                }}</span>
                <span
                  v-if="cotizacion.numero_chasis"
                  class="mb-1 text-sm font-light"
                >
                  <i class="fal fa-barcode fa-fw text-gray-600"></i>
                  Nº Chasis {{ cotizacion.numero_chasis }}
                </span>
                <span v-if="cotizacion.anio" class="mb-1 text-sm font-light">
                  <i class="fal fa-calendar fa-fw text-gray-600"></i>
                  Año {{ cotizacion.anio }}
                </span>
                <span v-if="cotizacion.color" class="mb-1 text-sm font-light">
                  <i class="fal fa-palette fa-fw text-gray-600"></i>
                  Color {{ cotizacion.color }}
                </span>
              </div>
            </div>
          </div>

          <!-- Datos del Distribuidor -->
          <div class="px-4 pt-2 pb-4 text-gray-800">
            <div v-show="tieneDistribuidor">
              <hr class="h-px w-full border-t border-gray-200" />
              <div class="flex flex-col">
                <span
                  class="mt-1 text-sm font-semibold text-gray-700 uppercase"
                >
                  {{
                    cotizacion.distribuidor
                      ? cotizacion.distribuidor.nombre
                      : ""
                  }}
                </span>
                <span class="mt-1 text-sm font-light">
                  {{
                    cotizacion.distribuidor
                      ? cotizacion.distribuidor.direccion
                      : "-"
                  }},
                  {{
                    cotizacion.distribuidor
                      ? cotizacion.distribuidor.ciudad
                      : ""
                  }},
                  {{ cotizacion.region }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Datos del Cliente -->
        <div
          class="flex flex-col w-full lg:flex-1 border-t border-gray-400 ml-0 mb-4 lg:ml-2"
        >
          <div class="bg-gray-100 border-b border-gray-400 py-2 px-2">
            <p class="uppercase text-sm font-light text-gray-800">
              Datos del cliente
            </p>
          </div>
          <div class="p-4 flex flex-col leading-normal text-gray-800">
            <span class="text-lg font-semibold text-gray-700">{{
              cotizacion.nombre
            }}</span>
            <span
              v-if="cotizacion.extra && cotizacion.extra.rut"
              class="mt-1 text-sm font-light"
            >
              <i class="fal fa-id-card fa-fw text-gray-600"></i>
              {{ cotizacion.extra.rut }}
            </span>
            <span class="mt-1 text-sm font-light">
              <i class="fal fa-at fa-fw text-gray-600"></i>
              {{ cotizacion.email }}
            </span>
            <span class="mt-1 text-sm font-light">
              <i class="fal fa-phone fa-fw text-gray-600"></i>
              {{ cotizacion.telefono }}
            </span>
            <div class="mt-1 text-sm font-light">
              <i class="fal fa-map-marker-alt fa-fw text-gray-600"></i>
              {{ cotizacion.region }}
            </div>
            <div
              class="flex flex-col"
              v-if="cotizacion.extra && cotizacion.extra.financiamiento"
            >
              <span class="mt-1 text-sm font-light">
                <i class="fal fa-check-square fa-fw text-gray-600"></i> Busca
                financiamiento
              </span>
              <span
                v-if="cotizacion.extra.situacion_laboral"
                class="mt-1 text-sm font-light"
              >
                <i class="fal fa-briefcase fa-fw text-gray-600"></i> Trabajador
                {{ cotizacion.extra.situacion_laboral.toLowerCase() }}
              </span>
              <span
                v-if="cotizacion.extra.renta_liquida"
                class="mt-1 text-sm font-light"
              >
                <i class="fal fa-hand-holding-usd fa-fw text-gray-600"></i>
                Renta líquida $
                {{ cotizacion.extra.renta_liquida | prettyNumber }}
              </span>
              <span v-if="cotizacion.extra.pie" class="mt-1 text-sm font-light">
                <i class="fal fa-money-check-alt fa-fw text-gray-600"></i> Pie $
                {{ cotizacion.extra.pie | prettyNumber }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="cotizacion.pdf" class="flex justify-center">
        <a
          class="w-full md:w-auto bg-custom-color hover:bg-custom-color-hover text-white uppercase py-3 px-5 mb-4 focus:outline-none focus:shadow-outline"
          target="_blank"
          :href="cotizacion.pdf"
          >Descargar Cotización</a
        >
      </div>

      <!-- Mensajería -->
      <div
        v-show="cotizacion !== null"
        class="flex flex-col w-full border-t border-gray-400 mb-8"
      >
        <div class="bg-gray-100 py-2 px-2 border-b border-gray-400">
          <p class="uppercase text-sm font-light text-gray-800">Mensajes</p>
        </div>
        <div class="p-4 flex flex-col">
          <div
            v-for="mensaje in mensajes"
            :key="mensaje.id"
            style="border-bottom: 1px solid #f8fafc"
            class="flex flex-wrap w-full p-2 mb-2 border-b"
            :class="{
              'bg-blue-100 border-l-4 border-blue-500':
                mensaje.user_id !== null && mensaje.estado === 0,
            }"
          >
            <div class="bg-cover rounded">
              <img
                class="w-12 h-12 rounded-full mr-4"
                :src="mensaje.avatar"
                :alt="mensaje.nombre"
              />
            </div>
            <div class="flex flex-col flex-1">
              <div class="flex flex-wrap justify-between">
                <span class="text-base font-semibold text-gray-700">{{
                  mensaje.nombre
                }}</span>
                <div class="flex flex-wrap flex-col md:flex-row">
                  <span
                    v-show="mensaje.user_id !== null && mensaje.estado === 0"
                    class="info-noleido p-1 bg-blue-400 text-xs text-white mr-3 text-center"
                    >Nuevo mensaje</span
                  >
                  <span class="text-sm text-gray-600">{{
                    mensaje.fecha_creacion | formatDate("LLL")
                  }}</span>
                </div>
              </div>
              <div
                class="text-light text-gray-700"
                v-html="mensaje.mensaje"
              ></div>
              <div
                v-if="mensaje.adjuntos && mensaje.adjuntos.length > 0"
                class="mt-2 pt-2 border-t border-gray-200 bg-gray-100"
              >
                <div class="flex flex-wrap">
                  <!--div class="uppercase mb-2">Archivos adjuntos</div-->
                  <a
                    v-for="adjunto in mensaje.adjuntos"
                    :key="adjunto.id"
                    class="p-2 mx-2 text-blue-500 hover:underline"
                    target="_blank"
                    :href="adjunto.url"
                  >
                    <i class="fal fa-paperclip fa-fw text-gray-600"></i
                    >{{ adjunto.name }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <form id="mensajeForm" enctype="multipart/form-data">
            <div class="flex w-full py-2">
              <div class="bg-cover rounded">
                <v-gravatar
                  class="w-12 h-12 rounded-full mr-4"
                  :alt="cotizacion.nombre"
                  :email="cotizacion.email"
                  default-img="mm"
                />
              </div>
              <div class="flex flex-col flex-1 items-start">
                <div class="editor w-full text-base">
                  <editor-menu-bar :editor="editor">
                    <div
                      class="menubar p-2 bg-gray-200 border border-gray-400"
                      slot-scope="{ commands, isActive }"
                    >
                      <button
                        class="menubar__button"
                        title="Negrita"
                        :class="{ 'is-active': isActive.bold() }"
                        @click.prevent="commands.bold"
                      >
                        <i class="fal fa-bold"></i>
                      </button>
                      <button
                        class="menubar__button"
                        title="Cursiva"
                        :class="{ 'is-active': isActive.italic() }"
                        @click.prevent="commands.italic"
                      >
                        <i class="fal fa-italic"></i>
                      </button>
                      <button
                        class="menubar__button"
                        title="Tachar"
                        :class="{ 'is-active': isActive.strike() }"
                        @click.prevent="commands.strike"
                      >
                        <i class="fal fa-strikethrough"></i>
                      </button>
                      <button
                        class="menubar__button"
                        title="Subrayar"
                        :class="{ 'is-active': isActive.underline() }"
                        @click.prevent="commands.underline"
                      >
                        <i class="fal fa-underline"></i>
                      </button>

                      <!-- Headings -->
                      <button
                        class="menubar__button"
                        title="Encabezado de nivel 1"
                        :class="{ 'is-active': isActive.heading({ level: 1 }) }"
                        @click.prevent="commands.heading({ level: 1 })"
                      >
                        <i class="fal fa-h1"></i>
                      </button>
                      <button
                        class="menubar__button"
                        title="Encabezado de nivel 2"
                        :class="{ 'is-active': isActive.heading({ level: 2 }) }"
                        @click.prevent="commands.heading({ level: 2 })"
                      >
                        <i class="fal fa-h2"></i>
                      </button>
                      <button
                        class="menubar__button"
                        title="Encabezado de nivel 3"
                        :class="{ 'is-active': isActive.heading({ level: 3 }) }"
                        @click.prevent="commands.heading({ level: 3 })"
                      >
                        <i class="fal fa-h3"></i>
                      </button>

                      <!-- Lists -->
                      <button
                        class="menubar__button"
                        title="Lista"
                        :class="{ 'is-active': isActive.bullet_list() }"
                        @click.prevent="commands.bullet_list"
                      >
                        <i class="fal fa-list-ul"></i>
                      </button>
                      <button
                        class="menubar__button"
                        title="Lista ordenada"
                        :class="{ 'is-active': isActive.ordered_list() }"
                        @click.prevent="commands.ordered_list"
                      >
                        <i class="fal fa-list-ol"></i>
                      </button>

                      <button
                        class="menubar__button"
                        title="Deshacer"
                        @click.prevent="commands.undo"
                      >
                        <i class="fal fa-undo"></i>
                      </button>

                      <button
                        class="menubar__button"
                        title="Rehacer"
                        @click.prevent="commands.redo"
                      >
                        <i class="fal fa-redo"></i>
                      </button>
                    </div>
                  </editor-menu-bar>

                  <div
                    class="block w-full bg-white text-xl p-4 m-0 outline-none border-l border-r"
                  >
                    <editor-content
                      class="editor__content text-base"
                      :editor="editor"
                    />
                  </div>
                </div>

                <div
                  class="w-full border border-gray-400 border-dashed text-center"
                >
                  <div class="p-6 hover:bg-gray-100 relative">
                    <input
                      type="file"
                      name="archivos"
                      multiple
                      @change="actualizarListadoArchivos($event.target.files)"
                      :disabled="form.uploading"
                      class="absolute opacity-0 w-full h-full pin cursor-pointer"
                    />

                    <div v-show="!form.uploading" class="cursor-pointer">
                      <i class="fal fa-cloud-upload"></i>
                      Puedes adjuntar archivos arrastrándolos o haciendo click
                      aquí.
                    </div>

                    <div v-show="form.uploading">
                      <i class="fal fa-spin fa-spinner"></i> Cargando archivos,
                      espere un momento...

                      <div class="shadow w-full bg-gray-400 mt-2">
                        <div
                          class="bg-teal leading-none py-1 text-center text-white"
                          :style="`max-width: 100%; width: ${form.uploadPercentage}%`"
                        >
                          {{ form.uploadPercentage }}%
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-for="(file, index) in form.archivos"
                    :key="file.id"
                    class="flex items-start px-2 py-4 border-gray-400 hover:bg-gray-100 text-left"
                    :class="{
                      'border-t': index === 0,
                      'border-b': index !== form.archivos.length - 1,
                      'border-b-0': index === form.archivos.length - 1,
                    }"
                  >
                    <!-- Icon / Preview -->
                    <div class="flex-shrink">
                      <i
                        class="fal text-gray-700"
                        :class="obtenerIconoArchivo(file)"
                      ></i>
                    </div>
                    <!-- Nombre del archivo -->
                    <div class="flex-grow mx-4">
                      <div class="flex flex-col">
                        <div class="text-base">{{ file.name }}</div>
                        <div class="text-sm text-gray-600">
                          Tamaño:
                          <span class="font-semibold">
                            {{ file.size | formatBytes }}</span
                          >
                        </div>
                        <div v-show="!tieneTamanoPermitido(file)">
                          <div
                            class="text-custom-color border-l-4 border-red-600 bg-red-100 p-2 my-1"
                          >
                            El tamaño no puede ser superior a
                            <span class="font-semibold"
                              >{{ MAX_FILE_SIZE }} MB</span
                            >. El archivo no se cargará.
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Remover archivo -->
                    <div class="flex-shrink flex">
                      <button
                        @click.prevent="removerArchivo(index)"
                        class="bg-white text-gray-800 border border-gray-400 hover:text-custom-color hover:border-red-600 py-1 px-2 mr-2 outline-none"
                      >
                        <i class="fal fa-fw fa-trash-alt text-custom-color"></i>
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  class="uppercase text-white bg-teal-500 hover:bg-teal-600 mt-3 px-6 py-3"
                  :class="{ 'opacity-50 cursor-not-allowed': !puedeEnviar() }"
                  :disabled="!puedeEnviar()"
                  @click.prevent="enviarMensaje"
                >
                  Enviar
                  <span class="p-2" v-show="isLoading()"
                    ><i class="fal fa-spinner fa-spin"></i
                  ></span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BikeSportApi from "../Api";
import AuthUser from "./mixins/AuthUser";
import Loader from "./mixins/Loader";

import filesize from "filesize";

import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  Bold,
  BulletList,
  HardBreak,
  Heading,
  History,
  HorizontalRule,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Placeholder,
  Strike,
  Underline,
} from "tiptap-extensions";

export default {
  name: "DetalleCotizacion",
  mixins: [AuthUser, Loader],
  components: {
    EditorContent,
    EditorMenuBar,
  },
  data() {
    return {
      Api: null,
      idsNoLeidos: [],
      cotizacion: null,
      mensajes: [],
      MAX_FILE_SIZE: 2, // Tamaño máximo de un archivo adjunto en MB
      editor: new Editor({
        extensions: [
          new BulletList(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new BulletList(),
          new Bold(),
          new Italic(),
          new Link(),
          new Strike(),
          new Underline(),
          new History(),
          new Placeholder({
            emptyClass: "is-empty",
            emptyNodeText: "Escriba un mensaje...",
          }),
        ],
        onUpdate: ({ getHTML }) => {
          this.form.mensaje = getHTML();
        },
      }),
      form: {
        mensaje: null,
        archivos: [],
        dragAndDropCapable: false,
        dragAndDropInitialized: false,
        uploading: false,
        uploadPercentage: 0,
      },
    };
  },
  computed: {
    tieneDistribuidor() {
      return this.cotizacion.distribuidor !== undefined;
    },
  },
  filters: {
    formatBytes(bytes) {
      return filesize(bytes);
    },
  },
  methods: {
    actualizarEstadoMensajes() {
      this.mensajes.forEach((mensaje) => {
        if (mensaje.estado === 0 && mensaje.user_id !== null) {
          this.idsNoLeidos.push(mensaje.id);
        }
      });

      if (this.idsNoLeidos.length > 0) {
        this.Api.instance.request({
          url: this.Api.endpointPrefix + "/cotizacion/seguimiento/mensajes",
          method: "patch",
          params: { ids: this.idsNoLeidos.join(",") },
        });
      }
    },

    obtenerCotizacion(ticket) {
      const params = this.isAnonymous ? { hash: true } : {};
      return this.Api.instance.request({
        url: this.Api.endpointPrefix + "/cotizacion/seguimiento/" + ticket,
        method: "get",
        params: params,
      });
    },

    obtenerMensajes(ticket) {
      const params = this.isAnonymous ? { hash: true } : {};
      return this.Api.instance.request({
        url:
          this.Api.endpointPrefix +
          "/cotizacion/seguimiento/" +
          ticket +
          "/mensajes",
        method: "get",
        params: params,
      });
    },

    enviarMensaje() {
      const ticket = this.$route.params.ticket;
      const formData = this.getFormData();
      const params = this.isAnonymous ? { hash: true } : {};

      this.load();

      if (this.form.archivos.length > 0) {
        this.form.uploading = true;
      }

      this.Api.instance
        .request({
          url:
            this.Api.endpointPrefix +
            "/cotizacion/seguimiento/" +
            ticket +
            "/mensajes",
          method: "post",
          params: params,
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            this.form.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded * 100) / progressEvent.total)
            );
          },
        })
        .then((response) => {
          if (response.data.created) {
            if (this.idsNoLeidos.length > 0) {
              this.mensajes.forEach((mensaje) => {
                if (this.idsNoLeidos.includes(mensaje.id)) {
                  mensaje.estado = 1;
                }
              });
              this.idsNoLeidos = [];
            }
            this.mensajes.push(response.data.mensaje);

            this.form.archivos = [];
            this.form.mensaje = "";

            this.editor.clearContent(true);
          }
        })
        .catch(() => {
          //console.log(err);
        })
        .then(() => {
          this.loadComplete();

          this.form.uploading = false;
          this.form.uploadPercentage = 0;
        });
    },

    setData(promise) {
      this.load();

      promise
        .then((response) => {
          const data = response.data;

          if (data.success && data.cotizacion) {
            // Cotización
            this.cotizacion = data.cotizacion;
            // Mensajes asociados a la cotización
            this.obtenerMensajes(this.$route.params.ticket)
              .then((response) => {
                this.mensajes = response.data.mensajes;
                this.actualizarEstadoMensajes();
              })
              .catch(() => {
                //console.log("Error al obtener los mensajes", err);
              })
              .then(() => {
                this.loadComplete();
              });
          }
        })
        .catch(() => {
          //console.log("Error al obtener la cotización", err);
        });
    },

    getFormData() {
      let formData = new FormData();

      if (this.form.archivos.length > 0) {
        this.form.archivos.forEach((file, index) => {
          if (this.tieneTamanoPermitido(file)) {
            formData.append("archivos[" + index + "]", file);
          }
        });
      }

      formData.append("solicitud_id", this.cotizacion.id);
      formData.append("solicitud_type", this.cotizacion.prefijo);
      formData.append("email_cliente", this.cotizacion.email);
      formData.append("mensaje", this.form.mensaje);

      return formData;
    },

    removerArchivo(index) {
      this.form.archivos.splice(index, 1);
    },

    determineDragAndDropCapable() {
      const div = document.createElement("div");
      return (
        ("draggable" in div || ("ondragstart" in div && "ondrop" in div)) &&
        "FormData" in window &&
        "FileReader" in window
      );
    },

    obtenerIconoArchivo(file) {
      const imagesTypes = ["jpg", "jpeg", "png", "gif", "bmp"];
      const excelTypes = ["xls", "xlsx"];
      const wordTypes = ["doc", "docx"];
      const powerpointTypes = ["ppt", "pptx"];

      let classIcon = "fa-file";

      const ext = file.name.split(".").pop();

      if (imagesTypes.includes(ext)) {
        classIcon = "fa-file-image";
      } else if (excelTypes.includes(ext)) {
        classIcon = "fa-file-excel";
      } else if (wordTypes.includes(ext)) {
        classIcon = "fa-file-word";
      } else if (powerpointTypes.includes(ext)) {
        classIcon = "fa-file-powerpoint";
      } else if (ext === "pdf") {
        classIcon = "fa-file-pdf";
      }

      return classIcon;
    },

    actualizarListadoArchivos(files) {
      if (!files.length) return;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!this.esDuplicado(file)) {
          this.form.archivos.push(file);
        }
      }
    },

    esDuplicado(file) {
      return (
        this.form.archivos.findIndex((f) => {
          return f.name === file.name && f.size === file.size;
        }) !== -1
      );
    },

    tieneTamanoPermitido(file) {
      return file.size / 2e6 <= this.MAX_FILE_SIZE;
    },

    initAdjuntosDragAndDrop() {
      this.form.dragAndDropCapable = this.determineDragAndDropCapable();

      if (this.form.dragAndDropCapable && !this.form.dragAndDropInitialized) {
        const form = document.getElementById("mensajeForm");

        [
          "drag",
          "dragstart",
          "dragend",
          "dragover",
          "dragenter",
          "dragleave",
          "drop",
        ].forEach((evt) => {
          form.addEventListener(
            evt,
            (e) => {
              e.preventDefault();
              e.stopPropagation();
            },
            false
          );
        });

        form.addEventListener("drop", (e) => {
          for (let i = 0; i < e.dataTransfer.files.length; i++) {
            const file = e.dataTransfer.files[i];
            if (!this.esDuplicado(file)) {
              this.form.archivos.push(file);
            }
          }
        });

        this.form.dragAndDropInitialized = true;
      }
    },

    puedeEnviar() {
      return (
        this.form.mensaje &&
        this.form.mensaje.length > 0 &&
        this.form.mensaje !== "<p></p>" &&
        !this.form.uploading
      );
    },

    initBikeSportApi() {
      this.Api = new BikeSportApi();
    },
  },

  watch: {
    cotizacion(newValue, oldValue) {
      if (oldValue === null && newValue !== null) {
        this.$nextTick().then(() => {
          this.initAdjuntosDragAndDrop();
        });
      }
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.initBikeSportApi();
      vm.setData(vm.obtenerCotizacion(vm.$route.params.ticket));
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.initBikeSportApi();
    this.setData(this.obtenerCotizacion(this.$route.params.ticket));
    next();
  },

  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>
